import React, { useMemo, useState } from 'react'
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material'
import { useLocation } from 'react-router-dom'
import GlobalNavSidebar from './GlobalNavSidebar'
import { CodeWorkspaceLayoutContext, type LocalQueryContext } from './CodeWorkspaceLayoutContext'
import { colors, shell } from '../../design-system/tokens'
import { createTypographyStyle } from '../../design-system/tokens/typography'

interface MainLayoutProps {
  children: React.ReactNode
}

const MainLayout: React.FC<MainLayoutProps> = ({
  children
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const location = useLocation()
  const [codeWorkspace, setCodeWorkspace] = useState(false)
  const [localQuery, setLocalQuery] = useState<LocalQueryContext | null>(null)
  const workspaceLayout = useMemo(() => ({ codeWorkspace, setCodeWorkspace, localQuery, setLocalQuery }), [codeWorkspace, localQuery])
  const railWidth = codeWorkspace ? 56 : shell.rail.desktopWidth

  const getActiveSection = (): 'workspaces' | 'estate' | 'analytics' | 'agent' | undefined => {
    if (location.pathname.startsWith('/workspaces') || location.pathname.startsWith('/projects')) {
      return 'workspaces'
    }
    if (location.pathname.startsWith('/estate')) {
      return 'estate'
    }
    if (location.pathname.startsWith('/analytics')) {
      return 'analytics'
    }
    if (location.pathname.startsWith('/ai-canvas')) {
      return 'agent'
    }
    return undefined
  }

  return (
    <CodeWorkspaceLayoutContext.Provider value={workspaceLayout}>
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      width: '100vw',
      overflow: 'hidden',
      position: 'relative',
      '--archer-shell-footer-height': { xs: '56px', sm: '32px' },
    }}>
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <GlobalNavSidebar
          activeSection={getActiveSection()}
          compact={codeWorkspace}
        />

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            background: colors.background.page,
            overflow: 'hidden',
            height: 'calc(100vh - var(--archer-shell-footer-height))',
            marginLeft: isMobile ? 0 : `${railWidth}px`,
            minWidth: 0,
            paddingBottom: isMobile ? `${shell.rail.mobileHeight}px` : 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            {children}
          </Box>
        </Box>
      </Box>

      {/* Footer — full width, spanning under nav rail */}
      <Box
        component="footer"
        sx={{
          px: { xs: 1, sm: 3 },
          height: 'var(--archer-shell-footer-height)',
          borderTop: `1px solid ${colors.neutral[200]}`,
          backgroundColor: colors.background.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: { xs: 0, sm: 0.75 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <img src="/archer_icon.svg" alt="" style={{ width: 16, height: 16, opacity: 0.5 }} />
            <Typography sx={{ ...createTypographyStyle('bodySmall'), color: colors.text.secondary, fontWeight: 500 }}>
              Archer by Tenarai
            </Typography>
          </Box>
          <Typography aria-hidden="true" sx={{ ...createTypographyStyle('bodySmall'), color: colors.text.secondary, mx: 0.5, display: { xs: 'none', sm: 'block' } }}>·</Typography>
          <Typography sx={{ ...createTypographyStyle('bodySmall'), color: colors.text.secondary, textAlign: 'center' }}>
            {'\u00A9'} 2026 Tenarai. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Box>
    </CodeWorkspaceLayoutContext.Provider>
  )
}

export default MainLayout
